import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { updateSportProfileInput } from '@sorare/core/src/__generated__/globalTypes';
import {
  ShopItemType,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useUpdateSportProfile } from '@sorare/core/src/hooks/useUpdateSportProfile';

import type { ShopItemLogo } from '#src/components/shopItems/ShopItemDialog/types.ts';
import { Details } from '#src/components/shopItems/ShopItemPicker/ItemPreviewDialog/Details/index.tsx';
import { ItemImagePreview } from '#src/components/shopItems/ShopItemPicker/ItemPreviewDialog/ItemImagePreview/index.tsx';

import type { SkinContent_clubShopItem } from './__generated__/index.graphql.ts';

const CenteredLoadingButton = styled(LoadingButton)`
  align-self: center;
`;

type Props = {
  item: SkinContent_clubShopItem;
  itemEquipped?: boolean;
  onSelect?: (shopItem: ShopItemLogo) => void;
};

export const SkinContent = ({ item, itemEquipped, onSelect }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { sport } = useSportContext();
  const [loadingEquip, setLoadingEquip] = useState(false);
  const updateSportProfile = useUpdateSportProfile();

  const equipSkin = async () => {
    setLoadingEquip(true);

    const payload: updateSportProfileInput = { sport: sport || Sport.FOOTBALL };
    if (item.position === ShopItemType.CLUB_BADGE) {
      payload.clubBadgeId = item.id;
    } else if (item.position === ShopItemType.BACKGROUND) {
      payload.backgroundId = item.id;
    } else if (item.position === ShopItemType.ACHIEVEMENT_BADGE) {
      const currentBadgesIds = (
        currentUser?.anyUserSportProfile?.achievementBadges || []
      ).map(badge => badge?.id || null);
      const firstEmptySlotIndex = currentBadgesIds.findIndex(
        badge => badge === null
      );
      currentBadgesIds[firstEmptySlotIndex > 0 ? firstEmptySlotIndex : 0] =
        item.id;
      payload.achievementBadgesIds = currentBadgesIds;
    }
    await updateSportProfile(payload);
    setLoadingEquip(false);
  };

  return (
    <>
      <ItemImagePreview
        pictureUrl={item.pictureUrl}
        name={item.name}
        type={item.position}
      />
      <Details item={item} itemEquipped={itemEquipped} />
      <CenteredLoadingButton
        disabled={itemEquipped}
        loading={loadingEquip}
        color="primary"
        size="medium"
        onClick={() => {
          if (onSelect) {
            onSelect(item);
          } else {
            equipSkin();
          }
        }}
      >
        <FormattedMessage defaultMessage="Equip" />
      </CenteredLoadingButton>
    </>
  );
};

SkinContent.fragments = {
  clubShopItem: gql`
    fragment SkinContent_clubShopItem on ClubShopItem {
      ... on ShopItemInterface {
        id
        pictureUrl
        name
        position
        myPurchasesCount
        myLimitResetAt
        disabled
      }
      ...Details_shopItem
    }
    ${Details.fragments.shopItem}
  ` as TypedDocumentNode<SkinContent_clubShopItem>,
};
